import classnames from 'classnames'
import { memo, useEffect } from 'react'
import { useMediaQuery } from 'react-responsive'
import { GridItem, ListItem, AuctionStatusRibbon } from '../../../components/car-item'
import useFavorites from '../../../hooks/use-favorites'
import { ListingViewType } from '../config'

export default memo(function Listing({
  data,
  updatedData,
  viewType,
  setViewType,
  lastVehicleRef,
  isLoading,
}) {
  const isMobile = useMediaQuery({ query: '(max-width: 1024px)' })
  const { favorites, toggleFavorite, favoritesProcessing } = useFavorites()

  const isDataEmpty = data?.allIds?.length === 0

  useEffect(() => {
    if (isMobile && viewType !== ListingViewType.Grid) setViewType(ListingViewType.Grid)
  }, [isMobile, viewType, setViewType])

  return (
    <div className='wrapper'>
      {isDataEmpty && !isLoading && <p className='no-result-msg'> No results found</p>}

      {!isDataEmpty && (
        <div
          className={classnames('searchListing', {
            grid: viewType === ListingViewType.Grid,
          })}
        >
          {data?.allIds.map((vehicleId) => {
            const itemProps = getItemProps({
              vehicleId,
              data,
              updatedData,
              favorites,
              toggleFavorite,
              favoritesProcessing,
            })

            return viewType === ListingViewType.Grid ? (
              <GridItem key={vehicleId} {...itemProps} />
            ) : (
              <ListItem key={vehicleId} {...itemProps} />
            )
          })}

          <div ref={lastVehicleRef} style={{ minWidth: 2 }} />
        </div>
      )}

      <style jsx>{`
        .wrapper {
          padding: 0 1.2rem;
          overflow: hidden;
          background: var(--secondary-input-bg);

          .no-result-msg {
            text-align: center;
            max-width: 193rem;
            width: 100%;
            margin: 0 auto;
            padding: 3.8rem 4rem 0;
            font-size: 2.4rem;
            color: var(--secondary-text-color);
          }
        }

        .searchListing {
          max-width: 193rem;
          width: 100%;
          margin: 0 auto;
          padding: 3.8rem 4rem 1rem;

          &.grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, 44.6rem);
            grid-gap: 3.1rem 2.1rem;
            justify-content: center;
          }

          :global(.carListItem) {
            margin: 0 0 1rem;
          }
        }

        @media (max-width: 1024px) {
          .wrapper {
            .no-result-msg {
              font-size: 1.6rem;
            }
          }
          .searchListing {
            padding: 0;

            &.grid {
              grid-template-columns: auto;
              grid-gap: 1.4rem 2.1rem;
            }
          }
        }
      `}</style>
    </div>
  )
})

// ----------------------------------------------------------------------

function getItemProps({
  vehicleId,
  data,
  updatedData,
  favorites,
  toggleFavorite,
  favoritesProcessing,
}) {
  const vehicle = data.byId[vehicleId] || {}
  const financeAmount =
    updatedData[vehicleId]?.paymentDetails?.loanMonthlyPayment ||
    vehicle.paymentDetails?.loanMonthlyPayment
  let leaseAmount =
    updatedData[vehicle.id]?.paymentDetails?.leaseMonthlyPayment ||
    vehicle.paymentDetails?.leaseMonthlyPayment
  if (leaseAmount === financeAmount) {
    leaseAmount = false
  }
  return {
    id: vehicleId,
    name: vehicle.name,
    trim: vehicle.trim,
    imageFileKey: vehicle.mainPhotoUrl,
    price: updatedData[vehicleId]?.price || vehicle.price,
    mileage: vehicle.mileage,
    isFavorite: !!favorites[vehicleId]?.isFavorite,
    onFavorite: toggleFavorite,
    isFavoriteProcessing: !!favoritesProcessing[vehicleId]?.isProcessing,
    leaseAmount: leaseAmount,
    financeAmount: financeAmount,
    href: `/vehicle/${vehicle.id}`,
    status: updatedData[vehicleId]?.status || vehicle.status,
    ribbon: (
      <AuctionStatusRibbon
        status={updatedData[vehicleId]?.status || vehicle.status}
        auctionStartDate={vehicle.auctionStartDate}
        timeZone={vehicle.timeZone}
        reserveIsMet={vehicle.sellDetails?.soldAt}
      />
    ),
    year: vehicle.year,
  }
}
